
import {EarthServer} from "./bridge.js";
import {sharedStore} from "../framework/sharedStore.js";
import {sessionStore} from "../framework/sessionStore.js";
import {modalStore} from "../framework/modalStore.js";
import {isNumber, mapObject, pick} from "../lib/underscore.js";
import {deconstruct} from "../projection/projections.js";
import * as utc from "../util/utc.js";
import {annotate} from "../engine/annotator.js";
import * as d3 from "../lib/d3.js";

function convertScale(product) {
    const lo = product.valueInRange(0);
    const hi = product.valueInRange(1);
    function compute(val, units) {
        return units.scalarize(units.convert(val));
    }
    const range = mapObject(product.unitDescriptors, units => [compute(lo, units), compute(hi, units)]);
    return {type: product.scale.type, range, colors: product.scale.colors};
}

function convertUnits(units) {
    if (units.type === "enum") {
        return {
            type: "enum",
            name: units.name,
            elements: units.elements,
        };
    }
    return {
        type: "quantity",
        symbol: units.symbol,
        symbolHTML: units.html,
        precision: units.precision,
        convention: units.convention,
    };
}

function convertProduct(product) {
    const name = product.type;
    const source = product.sourceHTML;
    const desc = product.descriptionHTML;
    const description = [desc.name, desc.qualifier].filter(e => e).join("");
    const validTime = product.validTime?.();
    const units = mapObject(product.unitDescriptors, convertUnits);
    const timeline = product.timeline;
    const nav = product.navLabels;
    const supportedLevels = product.supported_z_levels;
    const result = {
        name,
        source,
        description,
        validTime: validTime !== undefined ? utc.print(validTime, "{YYYY}-{MM}-{DD} {hh}:{mm}Z") : undefined,
        units,
        timeline,
        nav,
        supportedLevels,
    };
    if (product.scale && product.valueInRange) {  // UNDONE: need better way to detect product download failure...
        result.scale = convertScale(product);
    }
    return result;
}

/**
 * @param primary
 * @param overlay
 * @param annotation
 * @return {array}
 */
function convertLayers(primary, overlay, annotation) {
    if (overlay === undefined && sharedStore.get("overlay_type") !== "none") {
        // the overlay and the animation are the same, i.e., wind
        overlay = primary;
    }
    return [
        primary !== undefined ? {type: "animation", product: convertProduct(primary)} : undefined,
        overlay !== undefined ? {type: "overlay", product: convertProduct(overlay)} : undefined,
        annotation !== undefined ? {type: "annotation", product: convertProduct(annotation)} : undefined,
    ];
}

function sampleLayerAt(layer, coords) {
    if (!layer) {
        return;
    }
    // UNDONE: support "sampling_method" per layer, perhaps as a parameter on the layer.
    const interpolate = layer.field?.()[sessionStore.get("interpolation_type")];
    const [λ = NaN, φ = NaN] = coords ?? [];
    const value = interpolate?.(λ, φ) ?? NaN;
    if (value !== value) {  // Bail for undefined, null, and NaN. Allow finite numbers and arrays to go through.
        return;
    }
    // HACK: test for fire sample. UNDONE: the sample schema really should be a feature of the layer/product.
    if (isNumber(value.frp)) {
        return value;
    }
    // This works for both quantities and enums...
    return Object.values(layer.unitDescriptors).map(units => ({
        value: units.convert(value),
        units: units.symbol ?? units.name,
    }));
}

/**
 * @param primary
 * @param overlay
 * @param annotation
 * @param coords
 * @return {array}
 */
function sampleLayersAt(primary, overlay, annotation, coords) {
    if (overlay === undefined && sharedStore.get("overlay_type") !== "none") {
        // the overlay and the animation are the same, i.e., wind
        overlay = primary;
    }
    return [
        sampleLayerAt(primary, coords),
        sampleLayerAt(overlay, coords),
        sampleLayerAt(annotation, coords),
    ];
}

/**
 * @param agents
 * @param inputController
 * @returns {EarthServer}
 */
export function buildServer(agents, inputController) {

    return new class EarthServerImpl extends EarthServer {

        getState() {
            return Object.assign({}, modalStore.getAll(), sessionStore.getAll(), sharedStore.getAll());
        }

        saveState(changes, meta) {
            const modal = modalStore.validate(pick(changes, modalStore.fields));
            const session = sessionStore.validate(pick(changes, sessionStore.fields));
            const shared = sharedStore.validate(pick(changes, sharedStore.fields));

            modalStore.save(modal, meta);
            sessionStore.save(session, meta);
            sharedStore.save(shared, meta);
        }

        getProjection() {
            const proj = agents.globeAgent.value()?.projection ?? undefined;
            return proj !== undefined ? deconstruct(proj) : undefined;
        }

        reorient(operation) {
            inputController.reorient(operation);
        }

        getLayers() {
            return convertLayers(
                agents.primaryLayerAgent.value(),
                agents.overlayLayerAgent.value(),
                agents.annotationLayerAgent.value(),
            );
        }

        sampleAt(point, coordinates) {
            const coords = coordinates ?? agents.globeAgent.value()?.projection.invert(point) ?? [NaN, NaN];
            return sampleLayersAt(
                agents.primaryLayerAgent.value(),
                agents.overlayLayerAgent.value(),
                agents.annotationLayerAgent.value(),
                coords,
            );
        }

        annotate(id, feature) {
            annotate(d3.select("#annotation"), id, feature, agents.globeAgent.value()?.projection);
        }
    };
}
